import React from 'react';

/**
 * Grilla cuyos elementos se reordenan arrastrandolos de su manija.
 *
 * Usa Pointer Events y no el drag & drop nativo de HTML5, porque ese no existe en touch y
 * buena parte de los vendedores publica desde el celular.
 *
 * El arrastre sale solo de la manija (`galeria-fotos__manija`, con `touch-action: none`) y
 * no de toda la tarjeta: si no, tocar una foto para scrollear la pagina en el celular
 * empezaria a moverla.
 *
 * <h3>Por que los listeners van en `document`</h3>
 * La primera version colgaba `onPointerMove`/`onPointerUp` de la manija y se apoyaba en
 * `setPointerCapture` para seguir recibiendolos al salir de ella. No funcionaba con mouse
 * real: apenas el cursor dejaba el icono el gesto se moria. Con los listeners en
 * `document` no hay nada que capturar — mientras el boton siga apretado los eventos
 * llegan igual, este el puntero donde este.
 *
 * Ademas se hace `preventDefault()` en el pointerdown: sin eso el navegador arranca su
 * propio arrastre nativo (de la imagen o de la seleccion de texto) y dispara
 * `pointercancel`, que mataba el gesto a los pocos pixeles.
 *
 * Props:
 *  - `items`: la lista a dibujar.
 *  - `renderItem(item, index, { arrastrando, destino, manijaProps })`: cada elemento; hay
 *    que colgar `manijaProps` del nodo que haga de manija, y `data-indice={index}` del
 *    nodo raiz de cada item (es lo que se busca debajo del puntero).
 *  - `onReordenar(desde, hasta)`: se llama al soltar, solo si la posicion cambio.
 *  - `keyDe(item, index)`: clave de React.
 *  - `className`: clase del contenedor.
 */

/** Pixeles a recorrer antes de considerar que es un arrastre y no un toque. */
const UMBRAL_ARRASTRE = 6;

export default class GaleriaOrdenable extends React.Component {

	state = {
		indiceArrastrado: undefined,
		indiceDestino: undefined
	}

	contenedorRef = React.createRef();

	/** Datos del gesto en curso; fuera del state porque cambian en cada pointermove. */
	gesto = undefined;

	componentWillUnmount() {
		this.soltarListeners();
	}

	/** Sobre que elemento de la grilla esta el puntero, si es que esta sobre alguno. */
	indiceBajoPuntero = (x, y) => {
		const elemento = document.elementFromPoint(x, y);
		if (!elemento || !elemento.closest) return undefined;

		const item = elemento.closest('[data-indice]');
		if (!item || !this.contenedorRef.current || !this.contenedorRef.current.contains(item)) return undefined;

		const indice = Number(item.dataset.indice);
		return Number.isNaN(indice) ? undefined : indice;
	}

	tomarListeners = () => {
		document.addEventListener('pointermove', this.manejarPointerMove);
		document.addEventListener('pointerup', this.manejarPointerUp);
		document.addEventListener('pointercancel', this.manejarPointerCancel);
		// Si el navegador igual arranca un arrastre nativo, se corta aca.
		document.addEventListener('dragstart', this.prevenir);
	}

	soltarListeners = () => {
		document.removeEventListener('pointermove', this.manejarPointerMove);
		document.removeEventListener('pointerup', this.manejarPointerUp);
		document.removeEventListener('pointercancel', this.manejarPointerCancel);
		document.removeEventListener('dragstart', this.prevenir);
	}

	prevenir = (event) => event.preventDefault();

	manejarPointerDown = (index) => (event) => {
		// Solo boton principal: con el secundario se abre el menu contextual y el gesto
		// queda colgado.
		if (event.button !== undefined && event.button !== 0) return;

		// Corta el arrastre nativo y la seleccion de texto, que disparaban `pointercancel`.
		event.preventDefault();

		this.gesto = { x: event.clientX, y: event.clientY, index, activo: false };

		this.tomarListeners();
	}

	manejarPointerMove = (event) => {
		if (!this.gesto) return;

		if (!this.gesto.activo) {
			const distancia = Math.hypot(event.clientX - this.gesto.x, event.clientY - this.gesto.y);
			if (distancia < UMBRAL_ARRASTRE) return;

			this.gesto.activo = true;
			this.setState({ indiceArrastrado: this.gesto.index });
		}

		// Con el gesto activo el puntero manda: sin esto, en touch la pagina scrollea abajo
		// del dedo mientras se arrastra.
		if (event.cancelable) event.preventDefault();

		this.setState({ indiceDestino: this.indiceBajoPuntero(event.clientX, event.clientY) });
	}

	/** El gesto se corta solo (una llamada entrante, un gesto del sistema): no se reordena. */
	manejarPointerCancel = () => {
		this.gesto = undefined;
		this.soltarListeners();
		this.setState({ indiceArrastrado: undefined, indiceDestino: undefined });
	}

	manejarPointerUp = () => {
		const { indiceArrastrado, indiceDestino } = this.state;
		const { onReordenar } = this.props;

		const huboArrastre = !!this.gesto && this.gesto.activo;

		this.gesto = undefined;
		this.soltarListeners();
		this.setState({ indiceArrastrado: undefined, indiceDestino: undefined });

		if (huboArrastre && onReordenar
			&& indiceArrastrado !== undefined
			&& indiceDestino !== undefined
			&& indiceDestino !== indiceArrastrado) {
			onReordenar(indiceArrastrado, indiceDestino);
		}
	}

	render() {
		const { items, renderItem, keyDe, className } = this.props;
		const { indiceArrastrado, indiceDestino } = this.state;

		const manijaProps = (index) => ({
			onPointerDown: this.manejarPointerDown(index),
			// El resto del gesto lo escucha `document`; ver el comentario de arriba.
			onDragStart: this.prevenir,
			draggable: false
		});

		return (
			<div className={className} ref={this.contenedorRef}>
				{(items || []).map((item, index) => (
					<React.Fragment key={keyDe ? keyDe(item, index) : index}>
						{renderItem(item, index, {
							arrastrando: index === indiceArrastrado,
							destino: indiceArrastrado !== undefined && index === indiceDestino && index !== indiceArrastrado,
							manijaProps: manijaProps(index)
						})}
					</React.Fragment>
				))}
			</div>
		);
	}
}
